import type { LoginViewState, ViewExtra } from "../types";

/** Read query-string params from the current URL */
export const getParams = () => new URLSearchParams(window.location.search);

const VIEW_STATES: LoginViewState[] = [
  "login",
  "forgotPassword",
  "forgotPasswordSent",
  "verifyCode",
  "updatePassword",
  "resetPassword",
  "confirmEmail",
  "accountSelection",
];

const isViewState = (value: string | null): value is LoginViewState =>
  !!value && (VIEW_STATES as string[]).includes(value);

/** Reads the subset of query params used as `extra` view data, discarding anything unrecognised/invalid. */
const readViewExtra = (params: URLSearchParams): ViewExtra => {
  const daysRaw = params.get("passwordDays") ?? "";
  const statusRaw = params.get("passwordStatus") ?? "";
  const customerModeRaw = params.get("customerMode") ?? "";
  const extra: ViewExtra = {};
  if (/^\d+$/.test(daysRaw)) extra.passwordDays = daysRaw;
  if (statusRaw === "expired" || statusRaw === "expiring") extra.passwordStatus = statusRaw;
  if (customerModeRaw === "true" || customerModeRaw === "false") extra.customerMode = customerModeRaw;
  return extra;
};

/** Determine the initial view from the current URL pathname + params */
export const resolveInitialView = (): { view: LoginViewState; extra: ViewExtra } => {
  const path = window.location.pathname.toLowerCase();
  const params = getParams();

  // Token-bearing links from emails always win — these are real server routes, not client-driven.
  if (path.includes("/account/resetpassword") && params.get("token")) {
    return { view: "resetPassword", extra: {} };
  }
  if (path.includes("/account/confirmemail")) {
    return { view: "confirmEmail", extra: {} };
  }

  // All other in-flow views (verifyCode, updatePassword, accountSelection, forgotPassword...) are
  // client-driven: `handleViewChange` pushes `?view=` onto history so refresh/back/forward restore them.
  const viewParam = params.get("view");
  if (isViewState(viewParam) && viewParam !== "resetPassword" && viewParam !== "confirmEmail") {
    return { view: viewParam, extra: readViewExtra(params) };
  }

  return { view: "login", extra: {} };
};

/**
 * Builds a URL for `nextView`, preserving the current pathname and any unrelated query params
 * (e.g. `account`), while replacing only the view-tracking ones. Used to push a history entry on
 * every client-side view transition so refresh/back/forward work correctly.
 */
export const buildViewUrl = (view: LoginViewState, extra: ViewExtra = {}): string => {
  const params = getParams();
  ["view", "passwordDays", "passwordStatus", "customerMode"].forEach((key) => params.delete(key));

  if (view !== "login") {
    params.set("view", view);
  }
  if (extra.passwordDays !== undefined) params.set("passwordDays", extra.passwordDays);
  if (extra.passwordStatus !== undefined) params.set("passwordStatus", extra.passwordStatus);
  if (extra.customerMode !== undefined) params.set("customerMode", extra.customerMode);

  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ""}`;
};

